import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./contexts/AuthContext";
import { ROLE_ROUTES } from "./contexts/AuthContext";
import ExecutiveOverview from './pages/executive-overview';
import RevenueOperations from './pages/revenue-operations';
import MemberAnalyticsHub from './pages/member-analytics-hub';
import SuperAdminDashboard from './pages/super-admin-dashboard';
import GymOwnerPortal from './pages/gym-owner-portal';
import GymOwnerMembers from './pages/gym-owner-members';
import GymOwnerClasses from './pages/gym-owner-classes';
import GymOwnerPackages from './pages/gym-owner-packages';
import GymOwnerPOS from './pages/gym-owner-pos';
import GymOwnerSettings from './pages/gym-owner-settings';
import StaffTrainerInterface from './pages/staff-trainer-interface';
import MemberPortal from './pages/member-portal';
import TrainerDashboard from './pages/trainer-dashboard';
import LandingPage from './pages/landing-page';
import LoginPage from './pages/login-page';
import RegistrationPage from './pages/registration-page';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';
import VerifyEmail from './pages/verify-email';
import ForgotPasswordScreen from './pages/forgot-password-screen';
import ResetPasswordScreen from './pages/reset-password-screen';
import GuidedOnboardingFlow from './pages/guided-onboarding-flow';
import ProfileUpdatePage from './pages/profile-update-page';
import ChangePasswordPage from './pages/change-password-page';
import MemberProgress from './pages/member-progress';
import AccountSettings from './pages/account-settings';
import MemberProfile from './pages/member-profile';
import VendorPlanSelection from './pages/vendor-plan-selection';
import GymSetup from './pages/gym-setup';

// Redirect authenticated users away from auth pages
function PublicRoute({ children }) {
  const { user, loading, isEmailVerified, userRole } = useAuth();
  if (loading) return null;
  if (user && isEmailVerified && userRole) {
    return <Navigate to={ROLE_ROUTES?.[userRole] || '/gym-owner-portal'} replace />;
  }
  return children;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing-page" element={<LandingPage />} />
          <Route path="/login-page" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/registration-page" element={<PublicRoute><RegistrationPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password-screen" element={<ForgotPasswordScreen />} />
          <Route path="/reset-password-screen" element={<ResetPasswordScreen />} />

          {/* Super Admin only */}
          <Route path="/super-admin-dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/executive-overview" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <ExecutiveOverview />
            </ProtectedRoute>
          } />
          <Route path="/revenue-operations" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <RevenueOperations />
            </ProtectedRoute>
          } />
          <Route path="/member-analytics-hub" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <MemberAnalyticsHub />
            </ProtectedRoute>
          } />

          {/* Gym Owner (vendor) */}
          <Route path="/gym-owner-portal" element={
            <ProtectedRoute allowedRoles={['vendor', 'super_admin']}>
              <GymOwnerPortal />
            </ProtectedRoute>
          } />
          <Route path="/gym-owner-members" element={
            <ProtectedRoute allowedRoles={['vendor', 'super_admin']}>
              <GymOwnerMembers />
            </ProtectedRoute>
          } />
          <Route path="/gym-owner-classes" element={
            <ProtectedRoute allowedRoles={['vendor', 'super_admin']}>
              <GymOwnerClasses />
            </ProtectedRoute>
          } />
          <Route path="/gym-owner-packages" element={
            <ProtectedRoute allowedRoles={['vendor', 'super_admin']}>
              <GymOwnerPackages />
            </ProtectedRoute>
          } />
          <Route path="/gym-owner-pos" element={
            <ProtectedRoute allowedRoles={['vendor', 'super_admin']}>
              <GymOwnerPOS />
            </ProtectedRoute>
          } />
          <Route path="/gym-owner-settings" element={
            <ProtectedRoute allowedRoles={['vendor']}>
              <GymOwnerSettings />
            </ProtectedRoute>
          } />

          <Route path="/member-profile/:memberId" element={
            <ProtectedRoute allowedRoles={['vendor', 'super_admin', 'staff', 'trainer']}>
              <MemberProfile />
            </ProtectedRoute>
          } />

          {/* Staff & Trainer */}
          <Route path="/staff-trainer-interface" element={
            <ProtectedRoute allowedRoles={['staff', 'trainer', 'vendor', 'super_admin']}>
              <StaffTrainerInterface />
            </ProtectedRoute>
          } />

          {/* Trainer Dashboard */}
          <Route path="/trainer-dashboard" element={
            <ProtectedRoute allowedRoles={['trainer', 'vendor', 'super_admin']}>
              <TrainerDashboard />
            </ProtectedRoute>
          } />

          {/* Member */}
          <Route path="/member-portal" element={
            <ProtectedRoute allowedRoles={['member', 'vendor', 'super_admin']}>
              <MemberPortal />
            </ProtectedRoute>
          } />

          <Route path="/member-progress" element={
            <ProtectedRoute allowedRoles={['member', 'vendor', 'super_admin']}>
              <MemberProgress />
            </ProtectedRoute>
          } />

          <Route path="/guided-onboarding-flow" element={
            <ProtectedRoute allowedRoles={['member', 'vendor', 'super_admin']}>
              <GuidedOnboardingFlow />
            </ProtectedRoute>
          } />

          <Route path="/profile-update-page" element={
            <ProtectedRoute allowedRoles={['member', 'vendor', 'super_admin', 'staff', 'trainer']}>
              <ProfileUpdatePage />
            </ProtectedRoute>
          } />

          <Route path="/change-password" element={
            <ProtectedRoute allowedRoles={['member', 'vendor', 'super_admin', 'staff', 'trainer']}>
              <ChangePasswordPage />
            </ProtectedRoute>
          } />

          <Route path="/account-settings" element={
            <ProtectedRoute allowedRoles={['member', 'vendor', 'super_admin', 'staff', 'trainer']}>
              <AccountSettings />
            </ProtectedRoute>
          } />

          <Route path="/vendor-plan-selection" element={
            <ProtectedRoute allowedRoles={['vendor']}>
              <VendorPlanSelection />
            </ProtectedRoute>
          } />

          <Route path="/gym-setup" element={
            <ProtectedRoute allowedRoles={['vendor']}>
              <GymSetup />
            </ProtectedRoute>
          } />

          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
